const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const setMessage = (element, message, tone) => {
  if (!element) return

  if (!message) {
    element.textContent = ""
    element.hidden = true
    delete element.dataset.tone
    return
  }

  element.textContent = message
  element.dataset.tone = tone
  element.hidden = false
}

const mountIntegrationGallery = () => {
  document.querySelectorAll("[data-integration-gallery]").forEach((container) => {
    if (container.dataset.galleryMounted === "true") return
    container.dataset.galleryMounted = "true"

    const openButton = container.querySelector("[data-integration-gallery-open]")
    const closeButtons = Array.from(container.querySelectorAll("[data-integration-gallery-close]"))
    const panel = container.querySelector("[data-integration-gallery-panel]")
    const backdrop = container.querySelector("[data-integration-gallery-backdrop]")

    if (!openButton || !panel || !backdrop) return

    const close = () => {
      panel.hidden = true
      backdrop.hidden = true
      panel.setAttribute("aria-hidden", "true")
      openButton.setAttribute("aria-expanded", "false")
      document.removeEventListener("keydown", handleKeydown)
    }

    const open = () => {
      panel.hidden = false
      backdrop.hidden = false
      panel.setAttribute("aria-hidden", "false")
      openButton.setAttribute("aria-expanded", "true")
      document.addEventListener("keydown", handleKeydown)
    }

    const handleKeydown = (event) => {
      if (event.key === "Escape") close()
    }

    openButton.addEventListener("click", open)
    backdrop.addEventListener("click", close)
    closeButtons.forEach((button) => button.addEventListener("click", close))
  })
}

const mountIntegrationScopeEditor = () => {
  document.querySelectorAll("[data-integration-scope-editor]").forEach((container) => {
    if (container.dataset.scopeMounted === "true") return
    container.dataset.scopeMounted = "true"

    const buildingInputs = Array.from(container.querySelectorAll("[data-integration-scope-building-input]"))
    const planPills = Array.from(container.querySelectorAll("[data-integration-scope-plan-pill]"))
    const emptyState = container.querySelector("[data-integration-scope-plan-empty]")

    if (buildingInputs.length === 0 || planPills.length === 0) return

    const syncVisibility = () => {
      const selectedBuildingIds = new Set(
        buildingInputs.filter((input) => input.checked).map((input) => String(input.dataset.buildingId || ""))
      )

      let visiblePlanCount = 0

      planPills.forEach((pill) => {
        const planBuildingId = String(pill.dataset.planBuildingId || "")
        const hiddenByBuildingSelection = planBuildingId.length > 0 && selectedBuildingIds.has(planBuildingId)
        pill.hidden = hiddenByBuildingSelection
        if (!hiddenByBuildingSelection) visiblePlanCount += 1
      })

      if (emptyState) emptyState.hidden = visiblePlanCount > 0
    }

    buildingInputs.forEach((input) => input.addEventListener("change", syncVisibility))
    syncVisibility()
  })
}

const mountIntegrationEnabledToggle = () => {
  document.querySelectorAll("[data-integration-enabled-toggle]").forEach((container) => {
    if (container.dataset.enabledToggleMounted === "true") return
    container.dataset.enabledToggleMounted = "true"

    const input = container.querySelector("[data-integration-enabled-input]")
    const state = container.querySelector("[data-integration-enabled-state]")
    const message = document.querySelector("[data-integration-enabled-message]")
    if (!input) return

    const syncStateLabel = () => {
      if (state) state.textContent = input.checked ? "Active" : "Inactive"
    }

    input.addEventListener("change", async () => {
      const desiredEnabled = input.checked
      input.disabled = true
      syncStateLabel()
      setMessage(message, desiredEnabled ? "Activating..." : "Deactivating...", "working")

      try {
        const response = await fetch(container.dataset.url, {
          method: "PATCH",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken()
          },
          body: JSON.stringify({ enabled: desiredEnabled })
        })

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload.error || "Could not update integration status")

        input.checked = !!payload.enabled
        syncStateLabel()
        setMessage(message, payload.message || "Integration status updated.", payload.pending_authorization ? "working" : "success")
      } catch (error) {
        input.checked = !desiredEnabled
        syncStateLabel()
        setMessage(message, error.message, "error")
      } finally {
        input.disabled = false
      }
    })
  })
}

const mountIntegrationApiTest = () => {
  document.querySelectorAll("[data-integration-api-test]").forEach((container) => {
    if (container.dataset.apiTestMounted === "true") return
    container.dataset.apiTestMounted = "true"

    const modal = document.querySelector("[data-integration-api-test-modal]")
    const form = modal?.querySelector("[data-integration-api-test-form]")
    const pathInput = modal?.querySelector("[data-integration-api-test-path]")
    const feedback = modal?.querySelector("[data-integration-api-test-feedback]")
    const result = modal?.querySelector("[data-integration-api-test-result]")
    const resultPath = modal?.querySelector("[data-integration-api-test-result-path]")
    const resultBody = modal?.querySelector("[data-integration-api-test-result-body]")
    const openButton = container.querySelector("[data-integration-api-test-open]")
    const closeButtons = Array.from(modal?.querySelectorAll("[data-integration-api-test-close]") || [])
    const submitButton = modal?.querySelector("[data-integration-api-test-submit]")

    if (!modal || !form || !pathInput || !feedback || !result || !resultPath || !resultBody || !openButton || !submitButton) return

    const handleKeydown = (event) => {
      if (event.key === "Escape") close()
    }

    const close = () => {
      modal.hidden = true
      modal.setAttribute("aria-hidden", "true")
      document.removeEventListener("keydown", handleKeydown)
    }

    const open = () => {
      setMessage(feedback, "", null)
      result.hidden = true
      resultPath.textContent = ""
      resultBody.textContent = ""
      modal.hidden = false
      modal.setAttribute("aria-hidden", "false")
      document.addEventListener("keydown", handleKeydown)
      window.setTimeout(() => pathInput.focus(), 0)
    }

    openButton.addEventListener("click", open)
    closeButtons.forEach((button) => button.addEventListener("click", close))

    form.addEventListener("submit", async (event) => {
      event.preventDefault()
      const path = pathInput.value.trim()
      if (!path) {
        setMessage(feedback, "Enter a request path.", "error")
        return
      }

      submitButton.disabled = true
      setMessage(feedback, "Running GET request...", "working")
      result.hidden = true

      try {
        const response = await fetch(form.dataset.url, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken()
          },
          body: JSON.stringify({ path })
        })

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload.error || "API test failed")

        resultPath.textContent = payload.path || path
        resultBody.textContent = JSON.stringify(payload.response ?? {}, null, 2)
        result.hidden = false
        setMessage(feedback, "Request completed.", "success")
      } catch (error) {
        setMessage(feedback, error.message, "error")
      } finally {
        submitButton.disabled = false
      }
    })
  })
}

export const mountIntegrationForms = () => {
  mountIntegrationGallery()
  mountIntegrationScopeEditor()
  mountIntegrationEnabledToggle()
  mountIntegrationApiTest()

  document.querySelectorAll("[data-integration-test]").forEach((container) => {
    if (container.dataset.mounted === "true") return
    container.dataset.mounted = "true"

    const button = container.querySelector("[data-integration-test-button]")
    const message = container.querySelector("[data-integration-test-message]")
    if (!button) return

    button.addEventListener("click", async () => {
      button.disabled = true
      setMessage(message, "Testing...", "working")

      try {
        const response = await fetch(button.dataset.url, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": csrfToken()
          }
        })

        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload.error || "Connection test failed")

        setMessage(message, payload.message || "Success", "success")
      } catch (error) {
        setMessage(message, error.message, "error")
      } finally {
        button.disabled = false
      }
    })
  })
}
